import { Link } from 'react-router-dom';
import {
  Lightning,
  Trophy,
  Star,
  Brain,
  Sparkle,
  Fire,
  Target,
  ArrowRight,
  CheckCircle,
  GameController,
  Rocket,
  Medal,
  ChartLineUp,
  BookOpen,
  PencilLine,
  Clock,
  Users,
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';

import SEO from '../components/SEO';
import LessonCountShowcase from '../components/LessonCountShowcase';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

const stagger = (i) => ({
  ...fadeUp,
  transition: { duration: 0.4, delay: i * 0.1 },
});

export default function ForPupils() {
  return (
    <div className="min-h-screen" data-testid="for-pupils-page">
      <SEO
        title="For Students — Level Up Your GCSE Revision"
        description="Make GCSE revision fun. Earn XP, keep streaks, level up across 9 difficulty levels, and get AI feedback that explains step-by-step. Maths, English, Science and Geography are live now, with History and Spanish planned next."
        path="/for-pupils"
        image="https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/9vbonzqn_Pupils%20happy%20with%20grade9ready.png"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b-2 border-foreground">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-violet-600 to-indigo-800" />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 40%)',
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-white">
          <div className="flex flex-col lg:flex-row items-center gap-10">
            <motion.div
              className="flex-1 text-center lg:text-left"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/15 backdrop-blur rounded-full text-sm font-bold mb-6">
                <Sparkle size={16} weight="fill" className="text-amber-300" />
                For Students
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black leading-tight mb-6">
                Level up your
                <br />
                GCSE game
              </h1>

              <p className="text-lg opacity-90 mb-8 max-w-xl">
                Forget boring textbooks. Grade9Ready makes revision feel like a game — with XP,
                streaks, levels, and AI that actually helps you understand. Maths, English, Science
                and Geography are live now, with History and Spanish planned next.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link
                  to="/free-lesson"
                  className="bg-white text-primary px-8 py-3 rounded-xl border-2 border-white font-bold text-base flex items-center justify-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.2)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.2)] transition-all"
                  data-testid="pupil-cta-signup"
                >
                  Try a Free Lesson <Rocket size={18} weight="fill" />
                </Link>
              </div>

              <div className="flex flex-wrap gap-4 mt-10 justify-center lg:justify-start">
                {[
                  { icon: Target, text: '4 subjects live' },
                  { icon: Lightning, text: '9 levels' },
                  { icon: Brain, text: 'AI feedback' },
                  { icon: Trophy, text: 'Earn XP' },
                ].map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-sm"
                  >
                    <s.icon size={16} weight="fill" className="text-amber-300" />
                    <span>{s.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="flex-1"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_pathway-to-9/artifacts/9vbonzqn_Pupils%20happy%20with%20grade9ready.png"
                alt="Students celebrating with Grade9Ready"
                className="rounded-2xl border-2 border-white/20 shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Getting Started */}
      <section className="bg-card border-b-2 border-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <motion.div className="text-center mb-10" {...fadeUp}>
            <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-bold rounded-full mb-3">
              GETTING STARTED
            </span>

            <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-3">
              How you get set up
            </h2>

            <p className="text-muted-foreground text-base max-w-2xl mx-auto">
              Your parent creates your account and manages access. You then sign in and start
              learning straight away.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <motion.div
              className="neo-card p-6"
              {...stagger(0)}
              data-testid="pupil-path-parent-invites"
            >
              <div className="inline-flex items-center gap-2 text-sm font-bold text-primary mb-3">
                <Users size={16} weight="fill" /> Your parent sets you up
              </div>

              <h3 className="font-heading font-bold text-lg mb-3">Most common way</h3>

              <ol className="space-y-3 text-sm">
                <li className="flex gap-3">
                  <span className="w-6 h-6 shrink-0 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-xs">
                    1
                  </span>
                  <span>Your parent creates their account and adds you as a pupil.</span>
                </li>

                <li className="flex gap-3">
                  <span className="w-6 h-6 shrink-0 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-xs">
                    2
                  </span>
                  <span>
                    You get a welcome email with a one-click login link, or they give you your
                    password.
                  </span>
                </li>

                <li className="flex gap-3">
                  <span className="w-6 h-6 shrink-0 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-xs">
                    3
                  </span>
                  <span>
                    Sign in, pick Maths, English, Science or Geography, and start your first
                    lesson.
                  </span>
                </li>
              </ol>
            </motion.div>

            <motion.div
              className="neo-card p-6"
              {...stagger(1)}
              data-testid="pupil-path-learning"
            >
              <div className="inline-flex items-center gap-2 text-sm font-bold text-amber-600 dark:text-amber-400 mb-3">
                <Rocket size={16} weight="fill" /> Your learning space
              </div>

              <h3 className="font-heading font-bold text-lg mb-3">
                You focus on learning
              </h3>

              <p className="text-sm leading-relaxed">
                Your parent adds you as a pupil, chooses the subscription and shares your login
                details or welcome link. You keep your own progress, lessons and achievements.
              </p>
            </motion.div>
          </div>

          <motion.div
            className="neo-card p-6 bg-primary/5"
            {...fadeUp}
            data-testid="pupil-what-you-can-do"
          >
            <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-emerald-500" />
              What you can do on your account
            </h3>

            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {[
                'Take lessons across every live subject',
                'Submit written work for instant AI feedback',
                'Earn XP, build streaks, unlock badges',
                'Take End-of-Section tests',
                'Track your own progress and topic scores',
                'Record spoken English answers using audio',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <CheckCircle
                    size={14}
                    weight="bold"
                    className="text-emerald-500 mt-1 shrink-0"
                  />
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 pt-4 border-t">
              <p className="text-xs text-muted-foreground flex items-start gap-2">
                <Lightning
                  size={14}
                  weight="bold"
                  className="text-amber-500 mt-0.5 shrink-0"
                />
                <span>
                  <strong>One thing pupils can&rsquo;t do:</strong> buy, upgrade or cancel the
                  subscription. A parent manages billing on their account &mdash; safer for
                  everyone.
                </span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div className="text-center mb-12" {...fadeUp}>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-3">
            How it works
          </h2>
          <p className="text-muted-foreground text-lg">
            Three steps to smashing your GCSEs
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            {
              step: '1',
              color: 'bg-primary',
              icon: BookOpen,
              title: 'Learn the concept',
              desc: "Every topic starts with a clear tutorial, key points, and worked examples — whether it's Maths, English, Science or Geography. You can even listen to explanations read aloud.",
            },
            {
              step: '2',
              color: 'bg-amber-500',
              icon: PencilLine,
              title: 'Practice & level up',
              desc: 'Answer practice questions, build accuracy and move through harder tasks as your confidence improves.',
            },
            {
              step: '3',
              color: 'bg-emerald-500',
              icon: Brain,
              title: 'Get AI feedback',
              desc: 'Submit your answers to our AI teacher. It tells you what went well, what to improve, and gives you step-by-step help across every subject.',
            },
          ].map((s, i) => (
            <motion.div key={i} className="neo-card p-6 relative" {...stagger(i)}>
              <div
                className={`absolute -top-4 -left-2 w-10 h-10 ${s.color} rounded-full border-2 border-foreground flex items-center justify-center text-white font-black text-lg shadow-[2px_2px_0px_0px_rgb(var(--9ready-shadow))]`}
              >
                {s.step}
              </div>

              <div className="mt-4">
                <s.icon size={32} className="text-primary mb-3" />
                <h3 className="font-bold text-lg mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* XP & Gamification */}
      <section className="bg-card border-y-2 border-foreground">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div className="flex-1" {...fadeUp}>
              <span className="inline-flex items-center gap-2 px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full mb-4">
                <GameController size={14} weight="fill" /> GAMIFICATION
              </span>

              <h2 className="text-3xl font-heading font-bold mb-4">
                It's not just revision. It's a game.
              </h2>

              <p className="text-muted-foreground mb-6">
                Every question you answer — in any subject — earns you XP. Keep your streak
                going, unlock badges, and compete against yourself to reach Level 9 across your
                GCSEs.
              </p>

              <div className="space-y-4">
                {[
                  {
                    icon: Star,
                    color: 'text-amber-500',
                    title: '+10 XP per correct answer',
                    desc: 'Every right answer earns experience points that build your level.',
                  },
                  {
                    icon: Fire,
                    color: 'text-orange-500',
                    title: 'Daily streaks',
                    desc: 'Log in and practise every day to keep your streak alive.',
                  },
                  {
                    icon: Medal,
                    color: 'text-violet-500',
                    title: 'Badges & achievements',
                    desc: 'Earn badges for completing sections, reaching milestones, and mastering topics.',
                  },
                  {
                    icon: ChartLineUp,
                    color: 'text-emerald-500',
                    title: '9 difficulty levels',
                    desc: 'Start at Level 1 and work your way up. Level 9 means Grade 9 standard challenge.',
                  },
                ].map((g, i) => (
                  <motion.div key={i} className="flex items-start gap-4" {...stagger(i)}>
                    <g.icon size={24} weight="fill" className={`${g.color} mt-1 shrink-0`} />
                    <div>
                      <h4 className="font-bold text-sm">{g.title}</h4>
                      <p className="text-xs text-muted-foreground">{g.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div className="flex-1" {...fadeUp} transition={{ delay: 0.2 }}>
              <div className="neo-card p-6 bg-gradient-to-br from-primary/5 to-amber-50 dark:from-primary/10 dark:to-amber-900/10 max-w-sm mx-auto">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-primary rounded-full border-3 border-foreground mx-auto flex items-center justify-center shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))] mb-3">
                    <span className="text-white text-2xl font-black">7</span>
                  </div>
                  <h3 className="font-heading font-bold text-lg">Level 7</h3>
                  <p className="text-sm text-muted-foreground">1,240 XP earned</p>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Progress to Level 8</span>
                      <span className="font-bold">72%</span>
                    </div>

                    <div className="h-3 bg-secondary rounded-full border border-foreground overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-primary to-violet-500 rounded-full"
                        style={{ width: '72%' }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 pt-3">
                    <div className="text-center p-2 bg-secondary/50 rounded-lg">
                      <Fire size={20} weight="fill" className="text-orange-500 mx-auto" />
                      <div className="text-xs font-bold mt-1">14 days</div>
                      <div className="text-[10px] text-muted-foreground">Streak</div>
                    </div>

                    <div className="text-center p-2 bg-secondary/50 rounded-lg">
                      <Trophy size={20} weight="fill" className="text-amber-500 mx-auto" />
                      <div className="text-xs font-bold mt-1">8</div>
                      <div className="text-[10px] text-muted-foreground">Badges</div>
                    </div>

                    <div className="text-center p-2 bg-secondary/50 rounded-lg">
                      <Target size={20} weight="fill" className="text-emerald-500 mx-auto" />
                      <div className="text-xs font-bold mt-1">42/61</div>
                      <div className="text-[10px] text-muted-foreground">Topics</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What makes it different */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div className="text-center mb-12" {...fadeUp}>
          <h2 className="text-3xl font-heading font-bold mb-3">
            Why students love Grade9Ready
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              icon: Lightning,
              color: 'bg-amber-500',
              title: 'No boring lectures',
              desc: 'Short tutorials, interactive lessons, and questions that adapt to your level — in every live subject.',
            },
            {
              icon: Clock,
              color: 'bg-cyan-500',
              title: 'Quick sessions',
              desc: 'Practise a topic in short bursts, then move on to the next subject or skill.',
            },
            {
              icon: Sparkle,
              color: 'bg-violet-500',
              title: 'AI that actually helps',
              desc: 'Not just "wrong, try again". Our AI explains step-by-step — whether it is algebra, essay structure, science explanation or geography evaluation.',
            },
            {
              icon: Rocket,
              color: 'bg-rose-500',
              title: 'Made for Grade 9',
              desc: "Every question across every subject pushes you towards stronger GCSE performance. This isn't just revision — it's mastery.",
            },
          ].map((c, i) => (
            <motion.div key={i} className="neo-card p-5 text-center" {...stagger(i)}>
              <div
                className={`w-14 h-14 ${c.color} rounded-2xl border-2 border-foreground mx-auto flex items-center justify-center mb-4 shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]`}
              >
                <c.icon size={28} weight="fill" className="text-white" />
              </div>
              <h3 className="font-bold mb-1">{c.title}</h3>
              <p className="text-xs text-muted-foreground">{c.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Lesson count showcase */}
      <LessonCountShowcase
        heading="What you'll master"
        subheading="Maths, English, Science and Geography are live now, with structured topic learning, worked examples, practice, AI feedback and progress tracking. History and Spanish are planned next."
      />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <motion.div {...fadeUp}>
          <div className="inline-flex items-center gap-2 mb-4">
            <Lightning size={28} weight="fill" className="text-amber-500" />
            <Lightning size={36} weight="fill" className="text-primary" />
            <Lightning size={28} weight="fill" className="text-amber-500" />
          </div>

          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
            Ready to start your Grade 9 journey?
          </h2>

          <p className="text-muted-foreground text-lg mb-8 max-w-lg mx-auto">
            Sign up, pick a topic, and start earning XP. It's free to try.
          </p>

          <Link
            to="/free-lesson"
            className="neo-btn-primary px-10 py-4 text-lg font-bold inline-flex items-center gap-2"
            data-testid="pupil-cta-bottom"
          >
            Let's Go <Rocket size={20} weight="fill" />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}